import React from 'react';
import '../../App.css';
import '../../bootstrap.css';
import * as Database from '../../helpers/Database';
import { mergeFoodFormEntryWithIngredientFbEntry, convertAndRoundNutritionData, NUTRITION_DATA, NUTRITION_DATA_PRIMARY, fromExternalSource } from '../../helpers/Util';
import * as Elasticsearch from '../../helpers/esFoodParser';
import LoggedFoodsTable from './LoggedFoodsTable';
import { NOTES_TYPE } from './NotesBox2';
import { firestore } from 'firebase';
import { every } from 'lodash';
const _ = require('lodash');

export default class RecipeBuilder extends React.Component {
  constructor(props) {
    super(props);
    this.ingredientsTally = {};
    this.state = {
      recipeName: this.props.recipeName ? this.props.recipeName : '',
      servingNumber: this.props.totalServings ? this.props.totalServings : '',
      servingName: 'servings',
      ingredients: {},
      errorMessage: '',
      successMessage: '',
      recipeReport: '',
      everythingConfirmed: false,
      blockUIOnRecipe: '',
    }

    this._initRecipeCartListener(this.props.userId);
  }

  _checkIsEverythingConfirmed = (ingredients) => {
    let everythingConfirmed = true;
    for (let key in ingredients) {
      const ingredient = ingredients[key];
      if (!ingredient.isPlaceholder && (!ingredient.name || ingredient.calories == null || ingredient.calories == undefined)) {
        everythingConfirmed = false;
        break;
      }
    }

    if (everythingConfirmed != this.state.everythingConfirmed) {
      this.setState({
        everythingConfirmed: everythingConfirmed
      })
    }
  }

  _initRecipeCartListener = (userId) => {
    if (this.unsubRecipeCartListener) {
      this.unsubRecipeCartListener();
    }
    this.unsubRecipeCartListener = Database.initRecipeCartListener(userId, (ingredients) => {
      this._updateTallyAndReport(ingredients, this.state.recipeName, this.state.servingNumber, this.state.servingName);
      this.setState({
        ingredients
      });
      this._checkIsEverythingConfirmed(ingredients);
    }, this.props.logId);
  }

  componentWillUnmount() {
    if (this.unsubRecipeCartListener) {
      this.unsubRecipeCartListener();
    }

    if (this.unsubRecipeIndexedListener) {
      this.unsubRecipeIndexedListener();
    }
  }

  _recipeNameChange = (e) => {
    this._updateTallyAndReport(
      this.state.ingredients, e.target.value, this.state.servingNumber, this.state.servingName);
    this.setState({
      recipeName: e.target.value,
    });
  }

  _servingNumberChange = (e) => {
    this._updateTallyAndReport(
      this.state.ingredients, this.state.recipeName, e.target.value, this.state.servingName);
    this.setState({
      servingNumber: e.target.value
    });
  }

  _servingNameChange = (e) => {
    this._updateTallyAndReport(
      this.state.ingredients, this.state.recipeName, this.state.servingNumber, e.target.value);
    this.setState({
      servingName: e.target.value
    });
  }

  _addPlaceholderIngredient = async (e) => {
    await Database.updateIngredientInRecipeCart(
      this.props.userId,
      {
        isPlaceholder: true,
      },
      Date.now().toString(),
      this.props.logId
    );

    this.setState({
      successMessage: '', // Success message may still be on screen after a previously successful submit.
    })
  }

  _deleteIngredient = async (userId, targetDate, docId) => {
    // Ingore targetDate. It'll be undefined and is not relevant here.
    await Database.deleteIngredientFromRecipeCart(userId, docId, this.props.logId);
  }

  _editIngredient = async (userId, existingFirebaseEntry, newFoodFormEntry, docId) => {
    let mergedEntry = mergeFoodFormEntryWithIngredientFbEntry(existingFirebaseEntry, newFoodFormEntry);
    await Database.updateIngredientInRecipeCart(userId, mergedEntry, docId, this.props.logId);
  }

  _promptSubmitRecipe = () => {
    // LUCY: Display popup asking to create recipe.
    this._submitRecipe();
  }

  _submitRecipe = async () => {
    if (!this.state.recipeName || !this.state.servingName || !this.state.servingNumber) {
      this.setState({
        errorMessage: "Cannot be empty: Recipe name, serving number, and serving name."
      });
      return;
    }

    if (Object.keys(this.state.ingredients).length < 1) {
      this.setState({
        errorMessage: "Please add at least 1 ingredient."
      });
      return;
    }

    if (!this.ingredientsTally.calories || this.ingredientsTally.calories <= 0) {
      // This can happen if we only have placeholder foods.
      this.setState({
        errorMessage: "Recipe should not be 0 calories. Did you forget to add ingredients?"
      });
      return;
    }

    this.setState({
      errorMessage: ''
    });

    // This will save to elasticsearch 1 x <#> <servings> of <recipe name>. So that what shows in servings dropdown is something like
    // "8 cups" -- This helps to also communicate how many servings this recipe makes total without us having to refer back to Data notes all the time.
    let recipeFood = {
      name: this.state.recipeName,
      consolidatedServingName: `${this.state.servingNumber} ${this.state.servingName}`,
      serving: this.state.servingName,
      servingNumber: this.state.servingNumber,
      foodGroups: [],
      images: []
    };
    NUTRITION_DATA.forEach(dataName => {
      recipeFood[dataName] = this.ingredientsTally[dataName];
    });

    const recipeId = await Database.addRecipeFoodToIndex(
      this.props.userId,
      this.props.targetDate,
      this._prepForIndexing(recipeFood, this.state.ingredients),
    ); // This will trigger dbsync in Cloud Functions to index this food.

    for (let key in this.state.ingredients) { // Then add all the ingredients as well so we don't waaste time looking it up again if user reuses ingredients.
      let ingredient = this.state.ingredients[key];
      if (ingredient && ingredient.isPlaceholder !== true) {
        await Database.addRecipeFoodToIndex(
          this.props.userId,
          this.props.targetDate,
          this._prepForIndexing(ingredient)
        );
      }
    }

    await Database.appendToUserNotes(this.props.userId, NOTES_TYPE.DataEntry, this.state.recipeReport);

    if (this.props.emptyCartAfterSubmit) {
      Database.deleteEverythingFromRecipeCart(this.props.userId, this.props.logId);
    }

    this.setState({
      successMessage: `Success! The recipe breakdown has also been added to the bottom of Data Entry Notes.`,
      recipeName: '',
      servingNumber: '',
      servingName: 'serving',
      blockUIOnRecipe: recipeId // Block UI until the recipe actually gets indexed by server.
    })

    this.unsubRecipeIndexedListener = Database.doOnRecipeFoodIndexed(this.props.userId, this.props.targetDate, recipeId, (indexed) => {
      if (indexed) {
        this._unblockUIOnRecipe();
      }
    });
  }

  _unblockUIOnRecipe = () => {
    this.setState({
      blockUIOnRecipe: ''
    })

    if (this.unsubRecipeIndexedListener) {
      this.unsubRecipeIndexedListener();
    }

    // Now that recipe is fully indexed, we can "submit" the form, which should dismiss this recipe builder.
    if (this.props.onSubmitEverything) {
      this.props.onSubmitEverything();
    }
  }

  _prepForIndexing = (recipeFood, ingredients) => {
    // Add foodHistoryId if this is not from external source so server won't re-index the same thing.
    if (recipeFood.foodId && !fromExternalSource(recipeFood.foodId)) {
      recipeFood['foodHistoryId'] = recipeFood.foodId;
    }

    if (ingredients) {
      recipeFood.ingredients = [];
      for (let key in ingredients) {
        const ingredient = ingredients[key];
        const ingredientToIndex = {
          id: ingredient.foodId ? ingredient.foodId : '',
          name: ingredient.name ? ingredient.name : '',
          servingDescription: ingredient.consolidatedServingName ? ingredient.consolidatedServingName : '',
          calories: ingredient.calories ? ingredient.calories : 0,
          carbs: ingredient.carbs ? ingredient.carbs : 0,
          protein: ingredient.protein ? ingredient.protein : 0,
          fat: ingredient.fat ? ingredient.fat : 0,
          fiber: ingredient.fiber ? ingredient.fiber : 0,
          netCarbs: ingredient.netCarbs ? ingredient.netCarbs : 0,
          sugar: ingredient.sugar ? ingredient.sugar : 0,
          logNote: [],
        };
        if (this.props.logId && ingredient.notes && ingredient.notes.length > 0) {
          // logNote holds on to the last set of notes this user logged for this ingredient + logId combo so that we we retrieve this form the index and log it, the user
          // will see the ingredient notes on their end too (but only for this particular log id - we don't want to show ingredient notes every time they relog this recipe in the future).
          ingredientToIndex.logNote.push({
            logId: this.props.logId,
            note: ingredient.notes
          });
        }
        recipeFood.ingredients.push(ingredientToIndex);
      }
      recipeFood.category = 'recipe';
      recipeFood.totalServings = _.toNumber(recipeFood.servingNumber);
    }

    recipeFood.notes = null; // Set this to null because we don't want to index notes. Note: In cloud functions (dbsync.js), we never index detailsNotepad for this same reason.
    return recipeFood;
  }

  _updateTallyAndReport = (ingredients, recipeName, servingNumber, servingName) => {
    let recipeReport = `==${recipeName ? recipeName : '<recipe name>'}== (Makes ${servingNumber ? servingNumber : '<#>'} ${servingName})\n`;
    let ingredientsTally = {};

    NUTRITION_DATA.forEach(dataName => {
      ingredientsTally[dataName] = 0;
    });

    for (let id in ingredients) {
      let ingredient = ingredients[id];
      if (ingredient.isPlaceholder === true) {
        continue;
      }

      recipeReport += `- ${ingredient.consolidatedServingName} ${ingredient.name}`;

      NUTRITION_DATA_PRIMARY.forEach(dataName => {
        if (ingredient[dataName]) {
          ingredientsTally[dataName] += convertAndRoundNutritionData(dataName, ingredient[dataName]);
          recipeReport += ` | ${dataName}: ${ingredient[dataName]}`;
        } else {
          recipeReport += ` | ${dataName}: 0`;
        }
      });

      recipeReport += '\n';
    }

    recipeReport += 'Totals';

    for (let dataName of NUTRITION_DATA_PRIMARY) {
      recipeReport += ` | ${dataName}: ${ingredientsTally[dataName]}`;
    }

    this.ingredientsTally = ingredientsTally;
    this.setState({
      recipeReport
    });
    this._checkIsEverythingConfirmed(ingredients);
  }

  render() {
    const onCloseButton = this.props.onClose ?
      <div className='row' style={{ margin: '0' }}>
        <div className='col'>
          <button className='float-right' onClick={this.props.onClose}>
            Dismiss
          </button>
        </div>
      </div>
      : <span />;

    return (
      <div style={{ padding: '10px', backgroundColor: 'wheat' }}>
        {
          !_.isEmpty(this.state.blockUIOnRecipe) ? (
            <div style={{
              backgroundColor: 'rgba(150,150,150,0.5)', position: 'absolute', width: '100%', height: '200%',
              zIndex: '2', textAlign: 'center', top: '600px', fontSize: '80px', color: 'red', top: '0', left: '0'
            }}>Waiting...</div>) : (<span />)
        }

        {onCloseButton}
        <div style={{ marginBottom: '10px', marginTop: '10px', textAlign: 'center' }}>
          <input
            type="text"
            placeholder="Recipe name"
            value={this.state.recipeName}
            name="recipeName"
            onChange={this._recipeNameChange}
            style={{ marginRight: '10px' }}
          />
          <span style={{ marginRight: '10px' }}>
            Makes
          </span>
          <input
            type="number"
            placeholder="#"
            value={this.state.servingNumber}
            name="servingNumber"
            onChange={this._servingNumberChange}
            style={{ marginRight: '10px' }}
          />
          <input
            type="text"
            value={this.state.servingName}
            name="servingName"
            onChange={this._servingNameChange}
          />
        </div>
        <LoggedFoodsTable
          revisionHistoryTitle={'User wrote'}
          foods={this.state.ingredients}
          targetDate={this.props.targetDate}
          userId={this.props.userId}
          userStatus={this.props.userStatus}
          adminId={this.props.adminId}
          onDeleteFood={this._deleteIngredient}
          onCorrectFood={this._editIngredient}
          enableDateMigration={false}
          ignoreTimestamps={true}
          isIngredient={true}
        />
        <div style={{ marginBottom: '10px', textAlign: 'right' }}>
          <button onClick={this._addPlaceholderIngredient}>
            + Add Ingredient
          </button>
        </div>
        <div style={{ color: 'green', fontSize: '20px' }}>{this.state.successMessage}</div>
        <textarea rows='10'
          style={{ width: "-webkit-fill-available", fontSize: '12px', padding: '10px', margin: "10px", backgroundColor: "lightgrey" }}
          value={this.state.recipeReport}
          readonly
        >
        </textarea>
        <div style={{ color: 'red' }}>{this.state.errorMessage}</div>
        <div style={{ textAlign: 'center' }}>
          <button
            style={{
              width: '500px',
              fontSize: '20px',
              padding: '10px',
              margin: '10px',
            }}
            onClick={this._promptSubmitRecipe}
            disabled={!this.state.everythingConfirmed}
          >
            CREATE RECIPE
          </button>
        </div>
      </div>
    )
  }
}


// WEBPACK FOOTER //
// ./src/main/foodLogging/RecipeBuilder.js